export const logicalScreenSlots = [
  "PRIMARY",
  "SECONDARY",
  "TERTIARY",
  "AUXILIARY_1",
  "AUXILIARY_2",
] as const;

export type LogicalScreenSlot = (typeof logicalScreenSlots)[number];
export type WorkspaceProfileLevel = "system" | "role" | "organization" | "user" | "mission";
export type WorkspacePanelDisplayState = "maximized" | "minimized" | "open";

export interface WorkspacePanelConfiguration {
  readonly schemaVersion: number;
  readonly values: Readonly<Record<string, boolean | number | string | null>>;
}

export interface WorkspacePanel {
  readonly configuration: WorkspacePanelConfiguration;
  readonly dockedZone: string | null;
  readonly height: number;
  readonly id: string;
  readonly locked: boolean;
  readonly logicalScreen: LogicalScreenSlot;
  readonly panelType: string;
  readonly poppedOut: boolean;
  readonly state: WorkspacePanelDisplayState;
  readonly width: number;
  readonly x: number;
  readonly y: number;
  readonly zIndex: number;
}

export interface WorkspaceLayout {
  readonly panels: readonly WorkspacePanel[];
  readonly schemaVersion: 1;
}

export interface WorkspaceProfile {
  readonly id: string;
  readonly label: string;
  readonly layout: WorkspaceLayout;
  readonly level: WorkspaceProfileLevel;
  readonly version: number;
  readonly locked?: boolean;
}

export function isLogicalScreenSlot(value: string): value is LogicalScreenSlot {
  return logicalScreenSlots.some((slot) => slot === value);
}

export function freezeWorkspaceProfile(profile: WorkspaceProfile): WorkspaceProfile {
  return Object.freeze({
    ...profile,
    layout: Object.freeze({
      ...profile.layout,
      panels: Object.freeze(profile.layout.panels.map((panel) => Object.freeze({
        ...panel,
        configuration: Object.freeze({
          ...panel.configuration,
          values: Object.freeze({ ...panel.configuration.values }),
        }),
      }))),
    }),
  });
}

export const builtInSystemProfile = freezeWorkspaceProfile({
  id: "built-in-system",
  label: "Built-in safe workspace",
  level: "system",
  version: 1,
  locked: true,
  layout: {
    schemaVersion: 1,
    panels: [{
      configuration: { schemaVersion: 1, values: {} },
      dockedZone: null,
      height: 280,
      id: "shell-status-panel",
      locked: false,
      logicalScreen: "PRIMARY",
      panelType: "shell_status",
      poppedOut: false,
      state: "open",
      width: 440,
      x: 24,
      y: 24,
      zIndex: 1,
    }],
  },
});
